import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Globe, Building2, Briefcase, ShieldAlert, Activity, Users,
    ArrowRight, Server, ShieldCheck, DollarSign, Cctv, Loader2
} from 'lucide-react';

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalCompanies: 0, totalProjects: 0, activeLicenses: 0, totalCameras: 0
    });
    const [auditLogs, setAuditLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [serverStats, setServerStats] = useState({
        cpu: 0, memory: 0, disk: 0, dbStatus: 'online', s3Status: 'online', pm2Status: 'online'
    });
    const [statsLoading, setStatsLoading] = useState(true);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const fetchOverview = async () => {
        try {
            const response = await axios.get('http://localhost:4000/api/superadmin/overview', { headers });
            const data = response.data;
            setCompanies(data);
            
            let tProj = 0, aLic = 0, tCam = 0;
            data.forEach(company => {
                tProj += company.projects.length;
                company.projects.forEach(p => {
                    if (p.licenseStatus === 'active') aLic++;
                    tCam += p.totalCameras;
                });
            });
            setStats({
                totalCompanies: data.length, totalProjects: tProj, activeLicenses: aLic, totalCameras: tCam
            });
        } catch (err) {
            console.error("Error loading SuperAdmin overview:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/audit-logs', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAuditLogs(response.data.slice(0, 5)); // Solo últimos 5
        } catch (err) {
            console.error("Error loading audit logs:", err);
        } finally {
            setLogsLoading(false);
        }
    };

    const fetchServerStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:4000/api/superadmin/stats', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setServerStats(response.data);
        } catch (err) {
            console.error("Error loading server stats:", err);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        fetchOverview();
        fetchAuditLogs();
        fetchServerStats();

        const interval = setInterval(fetchServerStats, 5000);
        return () => clearInterval(interval);
    }, []);

    const calculateMRR = () => {
        let total = 0;
        companies.forEach(c => {
            c.projects.forEach(p => {
                if (p.licenseStatus === 'active') {
                    if (p.planType === 'Basic') total += 49;
                    else if (p.planType === 'Pro') total += 149;
                    else if (p.planType === 'Enterprise') total += 499;
                }
            });
        });
        return total.toLocaleString();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-theme-bg flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-500/20 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-purple-500 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans pb-20">
            <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
                
                {/* Header */}
                <div className="border-b border-theme-border/80 pb-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                            <Globe className="text-purple-500 animate-pulse" size={32} /> Consola Global
                        </h1>
                        <p className="text-theme-text-secondary text-xs mt-1">Panel de control unificado e indicadores clave de rendimiento (KPIs) del SaaS.</p>
                    </div>
                </div>

                {/* KPI Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div 
                        onClick={() => navigate('/admin/companies')}
                        className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden cursor-pointer hover:border-purple-500/30 transition-all duration-300"
                    >
                        <div className="absolute right-0 top-0 w-24 h-24 bg-purple-500/5 rounded-full blur-xl"></div>
                        <Building2 size={24} className="text-purple-400 mb-4" />
                        <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Clientes Activos</span>
                        <div className="flex justify-between items-end mt-2">
                            <span className="text-3xl font-black text-theme-text-primary">{stats.totalCompanies}</span>
                            <span className="text-purple-400 text-xs font-bold flex items-center gap-1">Gestionar <ArrowRight size={12} /></span>
                        </div>
                    </div>

                    <div 
                        onClick={() => navigate('/admin/companies')}
                        className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden cursor-pointer hover:border-blue-500/30 transition-all duration-300"
                    >
                        <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl"></div>
                        <Briefcase size={24} className="text-blue-400 mb-4" />
                        <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Obras Registradas</span>
                        <div className="flex justify-between items-end mt-2">
                            <span className="text-3xl font-black text-theme-text-primary">{stats.totalProjects}</span>
                            <span className="text-blue-400 text-xs font-bold flex items-center gap-1">Ver Obras <ArrowRight size={12} /></span>
                        </div>
                    </div>

                    <div 
                        onClick={() => navigate('/admin/billing')}
                        className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden cursor-pointer hover:border-emerald-500/30 transition-all duration-300"
                    >
                        <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-xl"></div>
                        <DollarSign size={24} className="text-emerald-450" />
                        <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block mt-4">MRR (Mensual Recurrente)</span>
                        <div className="flex justify-between items-end mt-2">
                            <span className="text-3xl font-black text-emerald-400">${calculateMRR()} <span className="text-xs text-theme-text-secondary">USD</span></span>
                            <span className="text-emerald-400 text-xs font-bold flex items-center gap-1">Facturación <ArrowRight size={12} /></span>
                        </div>
                    </div>

                    <div 
                        onClick={() => navigate('/admin/cameras')}
                        className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden cursor-pointer hover:border-indigo-500/30 transition-all duration-300"
                    >
                        <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl"></div>
                        <Cctv size={24} className="text-indigo-400 mb-4" />
                        <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Cámaras IoT Activas</span>
                        <div className="flex justify-between items-end mt-2">
                            <span className="text-3xl font-black text-theme-text-primary">{stats.totalCameras}</span>
                            <span className="text-indigo-400 text-xs font-bold flex items-center gap-1">Monitorear <ArrowRight size={12} /></span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Resumen del Servidor */}
                    <div className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                        <div className="flex justify-between items-center border-b border-theme-border/80 pb-3">
                            <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                                <Server size={18} className="text-purple-400" /> Monitoreo de Servidor en Vivo
                            </h3>
                            <button 
                                onClick={() => navigate('/admin/security')}
                                className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1"
                            >
                                Gestionar Servidor <ArrowRight size={12} />
                            </button>
                        </div>
                        {statsLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="animate-spin text-purple-500" size={24} />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div className="p-3 bg-theme-bg rounded-2xl border border-theme-border">
                                        <span className="text-[9px] text-theme-text-secondary block uppercase font-bold">Uso CPU</span>
                                        <strong className="text-lg text-theme-text-primary font-mono mt-1 block">{serverStats.cpu}%</strong>
                                    </div>
                                    <div className="p-3 bg-theme-bg rounded-2xl border border-theme-border">
                                        <span className="text-[9px] text-theme-text-secondary block uppercase font-bold">Uso Memoria</span>
                                        <strong className="text-lg text-theme-text-primary font-mono mt-1 block">{serverStats.memory}%</strong>
                                    </div>
                                    <div className="p-3 bg-theme-bg rounded-2xl border border-theme-border">
                                        <span className="text-[9px] text-theme-text-secondary block uppercase font-bold">Disco Libre</span>
                                        <strong className="text-lg text-theme-text-primary font-mono mt-1 block">{100 - serverStats.disk}%</strong>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-2">
                                    <div className="flex justify-between items-center text-xs text-theme-text-secondary">
                                        <span>Base de Datos PostgreSQL</span>
                                        <span className="text-emerald-450 font-bold flex items-center gap-1.5">
                                            <ShieldCheck size={14} /> ACTIVO
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs text-theme-text-secondary">
                                        <span>Almacenamiento S3 / MinIO</span>
                                        <span className="text-emerald-450 font-bold flex items-center gap-1.5">
                                            <ShieldCheck size={14} /> ACTIVO
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs text-theme-text-secondary">
                                        <span>Procesos del Core (PM2)</span>
                                        <span className="text-emerald-450 font-bold flex items-center gap-1.5">
                                            <Activity size={14} /> ONLINE
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Resumen de Auditoría */}
                    <div className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                        <div className="flex justify-between items-center border-b border-theme-border/80 pb-3">
                            <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                                <ShieldAlert size={18} className="text-purple-400" /> Registro de Auditoría Reciente
                            </h3>
                            <button 
                                onClick={() => navigate('/admin/security')}
                                className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1"
                            >
                                Ver Todo el Historial <ArrowRight size={12} />
                            </button>
                        </div>
                        {logsLoading ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="animate-spin text-purple-500" size={24} />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {auditLogs.length === 0 ? (
                                    <p className="text-theme-text-secondary text-xs italic text-center py-6">Sin registros de auditoría.</p>
                                ) : (
                                    auditLogs.map(log => (
                                        <div key={log.id} className="flex justify-between items-center text-xs bg-theme-bg/40 border border-theme-border p-2.5 rounded-xl">
                                            <div className="space-y-0.5">
                                                <span className="font-bold text-theme-text-primary">{log.user_name || 'Sistema'}</span>
                                                <span className="text-[10px] text-theme-text-secondary block font-mono">{new Date(log.created_at).toLocaleTimeString()}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="font-bold text-purple-400 uppercase text-[10px] tracking-wider bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">{log.action}</span>
                                                <span className="text-[10px] text-theme-text-secondary block mt-0.5">{log.entity_type}</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Clientes Creados Recientemente */}
                <div className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                    <div className="flex justify-between items-center border-b border-theme-border/80 pb-3">
                        <h3 className="text-base font-bold text-theme-text-primary flex items-center gap-2">
                            <Building2 size={18} className="text-purple-400" /> Clientes Registrados Recientemente
                        </h3>
                        <button 
                            onClick={() => navigate('/admin/companies')}
                            className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1"
                        >
                            Ver Todos los Clientes <ArrowRight size={12} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {companies.slice(0, 3).map(company => (
                            <div key={company.id} className="bg-theme-bg border border-theme-border p-4 rounded-2xl flex flex-col justify-between">
                                <div>
                                    <h4 className="font-bold text-sm text-theme-text-primary">{company.name}</h4>
                                    <p className="text-[10px] text-theme-text-secondary font-mono mt-0.5">{company.email || company.adminEmail}</p>
                                </div>
                                <div className="flex justify-between items-center mt-4 text-[10px] text-theme-text-secondary border-t border-theme-border pt-2">
                                    <span>Obras: {company.projects.length}</span>
                                    <span>Cámaras: {company.projects.reduce((acc, p) => acc + p.totalCameras, 0)}</span>
                                </div>
                            </div>
                        ))}
                        {companies.length === 0 && (
                            <div className="col-span-3 text-center py-6 text-theme-text-secondary italic">No hay clientes registrados en la plataforma.</div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SuperAdminDashboard;
